import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { campaignApi } from "../services/campaignApi";

const BORDER_COLOR = "#E8ECF1";

const recipientFilters = [
  { value: "all", label: "All recipients" },
  { value: "pending", label: "Pending" },
  { value: "sent", label: "Sent" },
  { value: "delivered", label: "Delivered" },
  { value: "read", label: "Read" },
  { value: "unread", label: "Unread" },
  { value: "failed", label: "Failed" },
  { value: "interacted", label: "Interacted" },
];

const formatStatus = (value) => {
  if (!value) return "unknown";
  return String(value).replace(/_/g, " ");
};

const statusChipColor = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "failed") return "error";
  if (normalized === "read") return "success";
  if (normalized === "delivered") return "success";
  if (normalized === "pending" || normalized === "queued" || normalized === "sending") return "warning";
  return "default";
};

const formatDateTime = (value, timezone = "Africa/Harare") => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: timezone,
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
};

function MetricCard({ label, value, sub }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: BORDER_COLOR,
        bgcolor: "white",
        height: "100%",
      }}
    >
      <Typography variant="body2" color="text.secondary" fontWeight={800}>
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={950} sx={{ lineHeight: 1.05, mt: 0.5 }}>
        {value}
      </Typography>
      {sub ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.7 }}>
          {sub}
        </Typography>
      ) : null}
    </Paper>
  );
}

export default function WhatsAppCampaignInsightsDialog({ open, campaign, onClose }) {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [recipientQuery, setRecipientQuery] = useState("");
  const [recipientStatus, setRecipientStatus] = useState("all");
  const [recipientPage, setRecipientPage] = useState(1);
  const [recipientMeta, setRecipientMeta] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
  });

  useEffect(() => {
    if (!open) return;

    setTab(0);
    setRecipientQuery("");
    setRecipientStatus("all");
    setRecipientPage(1);
  }, [open, campaign?.id]);

  useEffect(() => {
    if (!open || !campaign?.id) return;

    let active = true;

    const loadDetails = async () => {
      setLoading(true);
      setError("");

      try {
        const [detailResponse, historyResponse] = await Promise.all([
          campaignApi.get(campaign.id),
          campaignApi.history(campaign.id, { limit: 200 }),
        ]);

        if (!active) return;

        setDetail(detailResponse?.data?.campaign ?? null);
        setHistory(historyResponse?.data?.events ?? []);
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.message || err?.message || "Failed to load campaign details");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadDetails();

    return () => {
      active = false;
    };
  }, [open, campaign?.id]);

  useEffect(() => {
    if (!open || !campaign?.id) return;

    let active = true;
    const timeout = setTimeout(async () => {
      setRecipientsLoading(true);

      try {
        const response = await campaignApi.recipients(campaign.id, {
          status: recipientStatus,
          q: recipientQuery || undefined,
          page: recipientPage,
          per_page: 20,
        });

        if (!active) return;

        const payload = response?.data ?? {};
        setRecipients(Array.isArray(payload?.data) ? payload.data : []);
        setRecipientMeta({
          currentPage: Number(payload?.current_page ?? 1),
          lastPage: Number(payload?.last_page ?? 1),
          total: Number(payload?.total ?? 0),
        });
      } catch (err) {
        if (!active) return;
        setRecipients([]);
        setRecipientMeta({ currentPage: 1, lastPage: 1, total: 0 });
        setError(err?.response?.data?.message || err?.message || "Failed to load recipients");
      } finally {
        if (active) {
          setRecipientsLoading(false);
        }
      }
    }, recipientQuery ? 250 : 0);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [open, campaign?.id, recipientPage, recipientQuery, recipientStatus]);

  const timezone = detail?.timezone || campaign?.timezone || "Africa/Harare";

  const summaryRows = useMemo(() => {
    const total = Number(detail?.total ?? 0);
    const delivered = Number(detail?.delivered ?? 0);
    const read = Number(detail?.read ?? 0);
    const unread = Number(detail?.unread ?? Math.max(0, delivered - read));

    return [
      {
        label: "Audience",
        value: total.toLocaleString(),
        sub: `${Number(detail?.pending ?? 0).toLocaleString()} still queued or sending`,
      },
      {
        label: "Delivered",
        value: delivered.toLocaleString(),
        sub: `${Number(detail?.delivery_rate ?? 0).toFixed(1)}% delivery rate`,
      },
      {
        label: "Read",
        value: read.toLocaleString(),
        sub: `${Number(detail?.read_rate ?? 0).toFixed(1)}% read rate`,
      },
      {
        label: "Unread",
        value: unread.toLocaleString(),
        sub: `${Number(detail?.failed ?? 0).toLocaleString()} failed`,
      },
    ];
  }, [detail]);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={950}>
                {detail?.name || campaign?.name || "Campaign performance"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                Delivery, read, recipient, and campaign history breakdown
              </Typography>
            </Box>

            <Chip
              size="small"
              label={formatStatus(detail?.status || campaign?.status)}
              color={statusChipColor(detail?.status || campaign?.status)}
              sx={{ textTransform: "capitalize", fontWeight: 900 }}
            />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap">
            <Typography variant="caption" color="text.secondary">
              Schedule: {formatDateTime(detail?.schedule_at || campaign?.schedule_at, timezone)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Speed: {Number(detail?.messages_per_hour ?? campaign?.messages_per_hour ?? 0).toLocaleString()}/hr
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Interacted contacts: {Number(detail?.interacted_contacts ?? 0).toLocaleString()}
            </Typography>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ minHeight: 560 }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", fontWeight: 900 } }}
        >
          <Tab label="Summary" />
          <Tab label="Recipients" />
          <Tab label="History" />
        </Tabs>

        {loading ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : null}

        {!loading && tab === 0 ? (
          <Stack spacing={2.5}>
            <Grid container spacing={2}>
              {summaryRows.map((item) => (
                <Grid item xs={12} sm={6} md={3} key={item.label}>
                  <MetricCard label={item.label} value={item.value} sub={item.sub} />
                </Grid>
              ))}
            </Grid>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: "1px solid",
                borderColor: BORDER_COLOR,
              }}
            >
              <Stack spacing={1.2}>
                <Typography variant="body1" fontWeight={900}>
                  Campaign details
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Sent: {Number(detail?.sent ?? 0).toLocaleString()} • Delivered: {Number(detail?.delivered ?? 0).toLocaleString()} • Read: {Number(detail?.read ?? 0).toLocaleString()} • Unread: {Number(detail?.unread ?? 0).toLocaleString()} • Failed: {Number(detail?.failed ?? 0).toLocaleString()}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Last interaction: {formatDateTime(detail?.last_interaction_at, timezone)}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  History events recorded: {Number(detail?.history_events ?? 0).toLocaleString()}
                </Typography>
              </Stack>
            </Paper>
          </Stack>
        ) : null}

        {!loading && tab === 1 ? (
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Search recipient"
                value={recipientQuery}
                onChange={(event) => {
                  setRecipientQuery(event.target.value);
                  setRecipientPage(1);
                }}
                fullWidth
              />

              <TextField
                select
                label="Filter"
                value={recipientStatus}
                onChange={(event) => {
                  setRecipientStatus(event.target.value);
                  setRecipientPage(1);
                }}
                sx={{ minWidth: 220 }}
              >
                {recipientFilters.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            {recipientsLoading ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <CircularProgress size={28} />
              </Box>
            ) : recipients.length === 0 ? (
              <Alert severity="info">No recipients matched this campaign filter.</Alert>
            ) : (
              <>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: BORDER_COLOR,
                    overflow: "hidden",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 900 }}>Recipient</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Delivery</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Interaction</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Last update</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recipients.map((recipient) => (
                        <TableRow key={recipient.id} hover>
                          <TableCell>
                            <Typography fontWeight={900}>{recipient.contact_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {recipient.phone || recipient.email || "No phone/email"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Stack spacing={0.8}>
                              <Chip
                                size="small"
                                label={formatStatus(recipient.display_status)}
                                color={statusChipColor(recipient.display_status)}
                                sx={{ width: "fit-content", textTransform: "capitalize", fontWeight: 800 }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                Sent {formatDateTime(recipient.sent_at || recipient.scheduled_at, timezone)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {recipient.unread ? "Delivered but not read yet" : `Read ${formatDateTime(recipient.read_at, timezone)}`}
                              </Typography>
                              {recipient.error ? (
                                <Typography variant="caption" color="error.main">
                                  {recipient.error}
                                </Typography>
                              ) : null}
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" fontWeight={800}>
                              {Number(recipient.reply_conversation_count ?? 0).toLocaleString()} conversations
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {Number(recipient.inbound_message_count ?? 0).toLocaleString()} inbound messages
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" fontWeight={800}>
                              {formatDateTime(
                                recipient.last_interaction_at || recipient.read_at || recipient.delivered_at || recipient.sent_at,
                                timezone
                              )}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>

                <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    {recipientMeta.total.toLocaleString()} recipients matched
                  </Typography>

                  <Pagination
                    color="primary"
                    page={recipientMeta.currentPage}
                    count={Math.max(1, recipientMeta.lastPage)}
                    onChange={(_, page) => setRecipientPage(page)}
                  />
                </Stack>
              </>
            )}
          </Stack>
        ) : null}

        {!loading && tab === 2 ? (
          <Stack spacing={1.5}>
            {history.length === 0 ? (
              <Alert severity="info">No activity has been recorded for this campaign yet.</Alert>
            ) : (
              history.map((event) => (
                <Paper
                  key={event.id}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: BORDER_COLOR,
                  }}
                >
                  <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={900}>{event.title}</Typography>
                      {event.subtitle ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {event.subtitle}
                        </Typography>
                      ) : null}
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                      {formatDateTime(event.timestamp, timezone)}
                    </Typography>
                  </Stack>
                </Paper>
              ))
            )}
          </Stack>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
